/**
 * What an address field reports, and how a picked place becomes one. Pure,
 * so the rules can be tested without a screen.
 */
export type AddressData = {
  address1: string | null;
  address2: string | null;
  city: string | null;
  /** State/region short code, e.g. the US 2-letter "NY". */
  state: string | null;
  postalCode: string | null;
  country: string | null;
};

export type PlaceDetails = {
  formattedAddress: string;
  addressComponents: { longName: string; shortName: string; types: string[] }[];
};

/** Typed text: the line as she wrote it, and nothing else known. */
export function freeTextAddress(address1: string | null): AddressData {
  return {
    address1,
    address2: null,
    city: null,
    state: null,
    postalCode: null,
    country: null,
  };
}

/**
 * A picked place. The street is the number and route when the place has
 * one; otherwise the line is whatever the place is called — "Paris, France",
 * or "France" — so the box always shows what was picked. The country is the
 * ISO code, not the name: everything that reads it expects alpha-2.
 */
export function extractAddressFields(details: PlaceDetails): AddressData {
  let streetNumber = "";
  let route = "";
  let city: string | null = null;
  let state: string | null = null;
  let postalCode: string | null = null;
  let country: string | null = null;

  for (const component of details.addressComponents) {
    if (component.types.includes("street_number")) {
      streetNumber = component.longName;
    } else if (component.types.includes("route")) {
      route = component.longName;
    } else if (component.types.includes("locality")) {
      city = component.longName;
    } else if (component.types.includes("administrative_area_level_1")) {
      // The short code ("NY") is the state; the long name still stands in
      // for the city when the place returned no locality.
      state = component.shortName;
      if (!city) city = component.longName;
    } else if (component.types.includes("postal_code")) {
      postalCode = component.longName;
    } else if (component.types.includes("country")) {
      country = component.shortName || component.longName;
    }
  }

  const street = [streetNumber, route].filter(Boolean).join(" ").trim();
  return {
    address1: street || details.formattedAddress || null,
    address2: null,
    city,
    state,
    postalCode,
    country,
  };
}
